import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { canSeeMenuItem, hasPermission } from "@/features/auth/utils/permissions";
import { App_Url_New } from "@/shared/common/static";
import { IconButton, NavItemButton } from "@/shared/components";
import HeaderBrand from "@/shared/legacy/components/layout/Header/HeaderBrand";
import { useCapabilities } from "@/shared/hooks/useCapabilities";
import { getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { useAppSelector } from "@/shared/redux/store/hooks";
import {
  brokerOnlyNavItems,
  employeeOnlyNavItems,
  employerOnlyNavItems,
  navbarItemFeatures,
  navbarItems,
  partnerOnlyNavItems,
  rootOnlyNavItems,
} from "../../config/sidebar.config";

const isPathActive = (currentPath: string, itemPath: string) =>
  currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);

export default function SideBar(): React.JSX.Element {
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const permissions = user?.user?.role?.permissions || {};
  // HR holds every benefits-self grant; the employee pages are for people on the roll (Module 26).
  const isEmployerHr = hasPermission(permissions, "benefits-self", "manage");
  const { isModuleEnabled, viewer, audience, edition } = useCapabilities();
  // Phones start with the rail collapsed so the employee portal gets the width (Module 26).
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const sidebarClasses = getThemeComponentGroup("sidebar");
  const sidebarBgClass = sidebarClasses?.bgColor || "bg-theme-accent";
  const sidebarTextClass = sidebarClasses?.textColor || "text-theme-text";
  const activeItemBgClass = sidebarClasses?.activeItemBgColor || "bg-theme-text/10";
  const activeItemTextClass = sidebarClasses?.activeItemTextColor || sidebarTextClass;
  const inactiveItemTextClass = sidebarClasses?.inactiveItemTextColor || "text-theme-text/60";
  const toggleButtonBgClass =
    sidebarClasses?.toggleButtonBgColor || "bg-theme-text/10 hover:bg-theme-text/20";
  const toggleIconClass = sidebarClasses?.toggleIconColor || sidebarTextClass;

  const filteredNavItems = (navbarItems ?? []).filter((item) => {
    if (!item.module || !item.action) return false;
    const feature = navbarItemFeatures[item.id];
    if (feature && !isModuleEnabled(feature)) return false;
    // Audience: only root staff see insurer-side administration; partner pages
    // belong to channel partners; the employer and employee portals (Module 26)
    // belong to an employer's people.
    if (viewer && audience !== "ROOT" && rootOnlyNavItems.has(item.id)) return false;
    // Broker-era iframe pages stay out of an insurer deployment (quick win B10).
    if (edition === "INSURER" && brokerOnlyNavItems.has(item.id)) return false;
    if (viewer && audience !== "PARTNER" && partnerOnlyNavItems.has(item.id)) return false;
    if (viewer && audience !== "EMPLOYER" && employerOnlyNavItems.has(item.id)) return false;
    if (viewer && (audience !== "EMPLOYER" || isEmployerHr) && employeeOnlyNavItems.has(item.id))
      return false;
    return canSeeMenuItem(permissions, item.module, item.action);
  });

  const activeItemId = filteredNavItems.find((item) => isPathActive(pathname, item.path))?.id ?? "";

  const activeClassName = `${activeItemBgClass} border-r-4 border-current font-bold ${activeItemTextClass} ${sidebarClasses?.activeItem || ""}`;
  const inactiveClassName = `${inactiveItemTextClass} hover:bg-theme-text/5 ${sidebarClasses?.inactiveItem || ""}`;

  return (
    <aside
      className={`
         inset-y-0 left-0 z-40 transform transition-all duration-300 ease-in-out overflow-y-auto thin-scrollbar
        md:translate-x-0 border-r border-theme-border/50 flex flex-col
        ${sidebarBgClass} ${sidebarTextClass} ${sidebarClasses?.root || ""}
        ${isCollapsed ? "w-16" : "w-64"}
      `}
    >
      <div className={`mb-5 mt-5 px-4 ${isCollapsed ? "flex justify-center" : ""}`}>
        {!isCollapsed && (
          <HeaderBrand variant="sidebar" linkTo={App_Url_New.secured.frontlinks.dashboard} />
        )}
        {isCollapsed && (
          <div className="flex justify-center transform scale-50 origin-center">
            <HeaderBrand variant="sidebar" linkTo={App_Url_New.secured.frontlinks.dashboard} />
          </div>
        )}
      </div>

      <nav className="flex-1 pt-0">
        {filteredNavItems.map((item) => (
          <NavItemButton
            key={item.id}
            icon={<item.icon size={20} />}
            label={item.label}
            active={activeItemId === item.id}
            collapsed={isCollapsed}
            activeClassName={activeClassName}
            inactiveClassName={inactiveClassName}
            onClick={() => navigate(item.path)}
          />
        ))}
      </nav>

      <div className={`py-4 ${isCollapsed ? "flex justify-center" : "flex justify-end pr-4"}`}>
        <IconButton
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          size="sm"
          className={`h-8 w-8 ${toggleButtonBgClass} ${sidebarClasses?.toggleButton || ""}`}
          onClick={() => setIsCollapsed((current) => !current)}
          icon={
            isCollapsed ? (
              <ChevronRight size={20} className={toggleIconClass} />
            ) : (
              <ChevronLeft size={20} className={toggleIconClass} />
            )
          }
        />
      </div>
    </aside>
  );
}
